import axios from 'axios'
import createAuthRefreshInterceptor from 'axios-auth-refresh'
import { load } from 'recaptcha-v3'
import { AuthMissingError } from '@/utils/errors'
import { getPersistentAuth } from '@/utils/firebase-core'

export const axiosInstance = axios.create({
  baseURL: `${import.meta.env.VITE_API_URL}/rest/v1`,
  headers: { 'Content-Type': 'application/json' }
})

export const axiosBaseInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  headers: { 'Content-Type': 'application/json' }
})

function actionFromUrl(url: string): string {
  const fragments = url.split('?')[0].split('/')
  const segment = fragments[fragments.length - 1] || fragments[fragments.length - 2]
  return segment.replace(/[^a-zA-Z0-9/_]/g, '_')
}

const recaptchaInterceptor = async (config: any) => {
  const recaptcha = await load(import.meta.env.VITE_RECAPTCHA)
  const action = config.recaptchaAction ?? actionFromUrl(config.url)
  const token = await recaptcha.execute(action)
  config.headers['X-Recaptcha'] = token
  return config
}

axiosInstance.interceptors.request.use(recaptchaInterceptor)
axiosBaseInstance.interceptors.request.use(recaptchaInterceptor)

let authFailurePromise: Promise<void> | null = null
async function handleAuthFailure() {
  if (authFailurePromise) return authFailurePromise
  authFailurePromise = (async () => {
    const { useAuthStore } = await import('@/stores/useAuthStore')
    const authStore = useAuthStore()
    try {
      await authStore.logout()
    } catch { /* still redirect even if firebase signOut fails */ }
    const { default: router } = await import('@/router')
    if (router.currentRoute.value.name !== 'login') {
      await router.replace({ name: 'login' })
    }
  })().finally(() => {
    authFailurePromise = null
  })
  return authFailurePromise
}

const refreshAuth = async (failedRequest: any) => {
  try {
    const { useAuthStore } = await import('@/stores/useAuthStore')
    const authStore = useAuthStore()
    if (!authStore.refreshToken) {
      throw new AuthMissingError('No refresh token')
    }
    await authStore.refreshAccessToken()
    if (authStore.accessToken) {
      failedRequest.response.config.headers.Authorization = `Bearer ${authStore.accessToken}`
    } else {
      throw new AuthMissingError('Refresh yielded no access token')
    }
  } catch (error: any) {
    error.request = failedRequest
    await handleAuthFailure()
    throw new AuthMissingError('Not logged in')
  }
}

createAuthRefreshInterceptor(axiosInstance, refreshAuth, { statusCodes: [401, 403] })
createAuthRefreshInterceptor(axiosBaseInstance, refreshAuth, { statusCodes: [401, 403] })

const authInterceptor = async (request: any) => {
  if (request.url !== '/users/login_link/' && request.url !== '/users/refresh/') {
    const { useAuthStore } = await import('@/stores/useAuthStore')
    const authStore = useAuthStore()
    let token: string | null = null
    if (authStore.refreshToken && authStore.accessToken === null) {
      try {
        await authStore.refreshAccessToken()
      } catch (error) {
        await handleAuthFailure()
        throw new AuthMissingError('Not logged in')
      }
    }
    if (authStore.accessToken) {
      token = authStore.accessToken
    } else {
      const auth = await getPersistentAuth()
      if (auth.currentUser) {
        token = await auth.currentUser.getIdToken()
      }
    }
    if (!token) {
      await handleAuthFailure()
      throw new AuthMissingError('Not logged in')
    }
    request.headers.Authorization = `Bearer ${token}`
  }
  return request
}

axiosInstance.interceptors.request.use(authInterceptor)
axiosBaseInstance.interceptors.request.use(authInterceptor)
